import {computed, inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {ERole} from '../../shared/models/ERole.model';
import {TokenService} from './token.service';
import {Restaurant} from '../../features/restaurants/models/Restaurant.model';
import {User} from '../../features/users/models/User.model';
import {JwtToken} from '../../shared/models/JwtToken';

@Injectable({
  providedIn: 'root',
})
export class SessionManagerService {
  private readonly router = inject(Router);
  private readonly tokenService = inject(TokenService);

  readonly loggedIn = computed(() => this.tokenService.token() !== null);

  readonly currentUser = computed<User | null>(() => {
    const token = this.tokenService.decodedToken();
    if (!token) return null;
    return {
      id: token.id,
      firstName: token.firstName,
      lastName: token.lastName,
      email: token.sub,
      isAdmin: token.isAdmin,
    } as User;
  });

  readonly currentRestaurant = computed<Restaurant | null>(() => {
    const token = this.tokenService.decodedToken();
    if (!token) return null;
    return {
      id: token.restaurantId,
      slug: token.restaurantSlug,
    } as Restaurant;
  });

  readonly currentRole = computed<string | null>(() => {
    return this.tokenService.decodedToken()?.role ?? null;
  });

  readonly multipleRoles = computed(() => {
    const token = this.tokenService.decodedToken();
    return !!token && token.nbRoles > 0;
  });

  readonly admin = computed(() => this.currentRole() === ERole.ADMIN);

  readonly manager = computed(() => {
    const role = this.currentRole();
    return role === ERole.MANAGER || role === ERole.OWNER;
  });

  readonly owner = computed(() => this.currentRole() === ERole.OWNER);

  readonly worker = computed(() => this.currentRole() === ERole.WORKER);

  readonly driver = computed(() => this.currentRole() === ERole.DRIVER);

  saveToken(jwtToken: JwtToken) {
    this.tokenService.saveJwtToken(jwtToken);
  }

  isTokenExpired() {
    return this.tokenService.isExpired();
  }

  needsTokenRefresh() {
    return this.tokenService.needsRefresh();
  }

  signOut() {
    this.tokenService.destroyJwtToken();
    this.router.navigate(['auth']);
  }
}
